import { useState, useEffect } from 'react';
import { Source, Layer, Marker, Popup, useMap } from 'react-map-gl/mapbox';
import type { FillLayerSpecification, LineLayerSpecification } from 'mapbox-gl';
import { useGcsStore } from '../../../store/useGcsStore';
import type { ControlZone } from '../../../types/gcs/flightNetwork';
import { getMapElementZIndex, MapElement } from '../../../types/ui/mapElement';
import { getServerName, getServerNameCode } from '../../../config/gcs/gcsConfigs';
import { Text } from '../../../components/general/Text';

interface GcsControlZoneProps {
  gcsId: string;
}

// Zoom level threshold: above this level (zoomed out) show marker, below (zoomed in) show polygon
const CONTROL_ZONE_ZOOM_THRESHOLD = 14;

const CONTROL_ZONE_FILL_COLOR = '#B2B2B2';
const CONTROL_ZONE_STROKE_COLOR = '#ffffff';
const CONTROL_ZONE_FILL_OPACITY = 0.5;

const controlZoneFillLayer: FillLayerSpecification = {
  id: 'control-zone-fill',
  type: 'fill',
  source: 'control-zone',
  paint: {
    'fill-color': CONTROL_ZONE_FILL_COLOR,
    'fill-opacity': CONTROL_ZONE_FILL_OPACITY
  }
};

const controlZoneLineLayer: LineLayerSpecification = {
  id: 'control-zone-line',
  type: 'line',
  source: 'control-zone',
  paint: {
    'line-color': CONTROL_ZONE_STROKE_COLOR,
    'line-width': 3
  }
};

const controlZoneToGeoJSON = (controlZone: ControlZone) => {
  const coordinates = controlZone.points.map((p) => [p.lng, p.lat]);
  // Close the polygon by adding the first point at the end
  if (coordinates.length > 0) {
    coordinates.push(coordinates[0]);
  }
  return {
    type: 'Feature' as const,
    geometry: {
      type: 'Polygon' as const,
      coordinates: [coordinates]
    },
    properties: {}
  };
};

/**
 * Computes the true geometric centroid of the control zone polygon.
 *
 * A simple arithmetic mean of the vertices is inaccurate for irregular polygons
 * because it ignores how the vertices are distributed along the edges — vertices
 * that are close together effectively get less "weight" than they should.
 *
 * Instead, we use the standard polygon centroid formula derived from the shoelace
 * algorithm:
 *   1. For each edge (i → j), compute the "cross" term: x_i * y_j - x_j * y_i.
 *   2. Sum those cross terms to get twice the signed area (shoelace formula).
 *   3. Weight each edge's midpoint by its cross term to accumulate cx and cy.
 *   4. Divide cx and cy by 6 * area to get the centroid.
 *
 * The signed area can be negative depending on vertex winding order (CW vs CCW),
 * but the division cancels the sign, so the result is always correct.
 *
 * Reference: https://en.wikipedia.org/wiki/Centroid#Of_a_polygon
 */
const calculateControlZoneCenter = (controlZone: ControlZone) => {
  const points = controlZone.points;
  if (points.length === 0) {
    return { lng: 0, lat: 0 };
  }

  let area = 0;
  let cx = 0;
  let cy = 0;
  const n = points.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n; // Wrap around: last vertex connects back to first
    const cross = points[i].lng * points[j].lat - points[j].lng * points[i].lat;
    area += cross;
    cx += (points[i].lng + points[j].lng) * cross;
    cy += (points[i].lat + points[j].lat) * cross;
  }

  area /= 2;

  // If area is effectively zero the points are collinear (degenerate polygon),
  // so fall back to the simple arithmetic mean to avoid dividing by ~0.
  if (Math.abs(area) < 1e-10) {
    const sum = points.reduce((acc, p) => ({ lng: acc.lng + p.lng, lat: acc.lat + p.lat }), { lng: 0, lat: 0 });
    return { lng: sum.lng / n, lat: sum.lat / n };
  }

  return {
    lng: cx / (6 * area),
    lat: cy / (6 * area)
  };
};

export const GcsControlZone = ({ gcsId }: GcsControlZoneProps) => {
  const { current: map } = useMap();
  const [zoom, setZoom] = useState<number>(map?.getZoom() ?? 10);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const controlZone = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.flightNetwork.data?.centerControlZone;
  });

  useEffect(() => {
    if (!map) return;

    const handleZoom = () => {
      setZoom(map.getZoom());
    };

    // Set initial zoom
    handleZoom();

    // Listen to zoom changes
    map.on('zoomend', handleZoom);

    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;

    const fillLayerId = `control-zone-fill-${gcsId}`;
    const handleLayerClick = () => setIsPopupOpen((prev) => !prev);
    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('click', fillLayerId, handleLayerClick);
    map.on('mouseenter', fillLayerId, handleMouseEnter);
    map.on('mouseleave', fillLayerId, handleMouseLeave);

    return () => {
      map.off('click', fillLayerId, handleLayerClick);
      map.off('mouseenter', fillLayerId, handleMouseEnter);
      map.off('mouseleave', fillLayerId, handleMouseLeave);
    };
  }, [map, gcsId]);

  if (!controlZone) {
    return null;
  }

  const center = calculateControlZoneCenter(controlZone);
  const showMarker = zoom < CONTROL_ZONE_ZOOM_THRESHOLD;
  const nameCode = getServerNameCode(gcsId);
  const name = getServerName(gcsId);

  const popup = isPopupOpen && (
    <Popup
      longitude={center.lng}
      latitude={center.lat}
      anchor='bottom'
      offset={32}
      className='marker-popup'
      closeButton={true}
      closeOnClick={true}
      onClose={() => setIsPopupOpen(false)}
    >
      <Text variant='body2'>{name}</Text>
    </Popup>
  );

  if (showMarker) {
    return (
      <>
        <Marker
          longitude={center.lng}
          latitude={center.lat}
          anchor='center'
          style={{ zIndex: getMapElementZIndex(MapElement.CONTROL_ZONE) }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '8px',
              backgroundColor: `rgba(178, 178, 178, ${CONTROL_ZONE_FILL_OPACITY})`,
              border: `3px solid ${CONTROL_ZONE_STROKE_COLOR}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={(event) => {
              event.stopPropagation();
              setIsPopupOpen((prev) => !prev);
            }}
          >
            <Text variant='body2-bold' style={{ color: 'white' }}>
              {nameCode}
            </Text>
          </div>
        </Marker>
        {popup}
      </>
    );
  }

  return (
    <>
      <Source id={`control-zone-${gcsId}`} type='geojson' data={controlZoneToGeoJSON(controlZone)}>
        <Layer {...controlZoneFillLayer} id={`control-zone-fill-${gcsId}`} source={`control-zone-${gcsId}`} />
        <Layer {...controlZoneLineLayer} id={`control-zone-line-${gcsId}`} source={`control-zone-${gcsId}`} />
      </Source>
      {popup}
    </>
  );
};
